<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>Chat</v-toolbar-title>
      <v-spacer />
      <v-btn icon dark @click="closeDialog">
        <v-icon color="navy">mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-alert v-if="messageSent && sendChatMessage" class="mx-4 mt-2" dense text type="success">
      <span>Message has been sent</span>
    </v-alert>
    <v-alert v-if="messageSent && addToLog" class="mx-4 mt-2" dense text type="success">
      <span>Log updated</span>
    </v-alert>

    <div class="d-flex">
      <div v-if="!removeImage && image" class="relative">
        <v-img :src="image" width="100" contain :lazy-src="image" class="ml-2 mt-4" />
        <v-btn class="img-close-icon" icon @click="removeImage = true">
          <v-icon>mdi-close-circle-outline</v-icon>
        </v-btn>
      </div>

      <j-emoji-textarea
        v-model="chatText"
        auto-grow
        dense
        rows="5"
        placeholder="Add your chat message"
        ref="textRef"
        class="flex-grow-1 ma-2"
      />
    </div>

    <v-btn
      class="ml-2 mb-4"
      color="primary"
      @click="scheduleMessage"
      :loading="loading"
      :disabled="!chatText || !selectedCoachingChatId"
      >{{ sendChatMessage ? "Send Message" : "Update Log" }}</v-btn
    >

    <div class="d-flex justify-space-between">
      <v-select
        v-model="selectedCoachingChatId"
        style="max-width: 250px;"
        item-text="title"
        item-value="id"
        class="mx-2 mt-n1"
        label="Chat"
        :items="chatsForUser"
      >
      </v-select>
      <v-btn
        x-small
        class="mr-4 mt-2"
        :href="`${$appEndPoint}/chats/${selectedCoachingChatId}`"
        target="_blank"
        outlined
        color="secondary"
      >
        Open Chat
      </v-btn>
    </div>
    <v-checkbox class="ml-2 mt-n3" v-model="sendChatMessage" label="Send Chat Message" />
    <v-checkbox class="ml-2 mt-n3" v-model="sendEmailNotification" label="Send email" />
    <v-checkbox class="ml-2 mt-n3" v-model="addToLog" label="Add Message to Log" />
  </v-card>
</template>

<script>
import { UPSERT_MESSAGE_MUTATION, CREATE_MESSAGE_ATTACHMENTS_MUTATION } from "@/constants/graphql/chat/chat";
import {
  STAFF_UPDATE_USER_ACTIVITY_MUTATION,
  STAFF_UPDATE_NUTRITION_LOG_MUTATION,
  STAFF_UPDATE_USER_REST_DAY_MUTATION,
} from "@/graphql/mutations/logging";
import { urlToDataURL, dataURLtoBlob, blobToFile } from "@/lib/file";
import { CHATS_FOR_USER } from "@/constants/graphql/chat/chat";
import { extractNodes } from "@/lib/array";
import { decodeId } from "@/lib/string";
import JEmojiTextarea from "@/components/shared/inputs/JEmojiTextarea";

export default {
  name: "SendMessageCard",
  components: {
    JEmojiTextarea,
  },
  props: {
    userId: {
      type: String,
      required: true,
    },
    coachingChatId: {
      type: [Number, String],
      required: false,
    },
    image: {
      type: String,
      required: false,
    },
    logType: {
      type: String,
      required: false,
    },
    logId: {
      type: [Number, String],
      required: false,
    },
    coachComment: {
      type: String,
      required: false,
    },
    // passing entire log now as well
    log: null,
  },
  data() {
    return {
      loading: false,
      chatText: this.coachComment,
      remoteImageDataUrl: null,
      messageSent: false,
      removeImage: false,
      sendEmailNotification: false,
      coachingChatIds: [],
      selectedCoachingChatId: this.coachingChatId,
      addToLog: true, // add this comment to the acitvity or nutrition log to showcase on the app
      sendChatMessage: true,
    };
  },
  computed: {
    loggedDate() {
      if (this.logType == "ACTIVITY" && this.log?.startDate) {
        return this.$moment(this.log?.startDate)?.format("dddd, DD MMM");
      } else if (this.logType == "NUTRITION" && this.log?.date) {
        return this.$moment(this.log?.date)?.format("dddd, DD MMM");
      } else if (this.logType == "ACTIVITY" && this.log?.restDayDate) {
        return this.$moment(this.log?.restDayDate)?.format("dddd, DD MMM");
      }
      return "";
    },
    loggedType() {
      if (this.logType == "ACTIVITY" && this.log?.type?.name) {
        return this.capitalizeFirstLetter(this.log?.type?.name ?? "");
      } else if (this.logType == "NUTRITION" && this.log.meal) {
        let mealString = this.capitalizeFirstLetter(this.log.meal ?? "");
        if (mealString == "Primary_snack" || mealString == "Secondary_snack") {
          return "Snack";
        }
        return mealString;
      } else if (this.logType == "ACTIVITY" && this.log?.restDayDate) {
        return "Rest Day";
      }
      return "";
    },
  },
  methods: {
    capitalizeFirstLetter(string) {
      string = string.toLowerCase();
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    closeDialog() {
      this.loading = false;
      this.removeImage = false;
      this.chatText = null;
      this.remoteImageDataUrl = null;
      this.messageSent = false;
      this.sendEmailNotification = false;
      this.$emit("closed");
    },
    async scheduleMessage() {
      if (this.addToLog) {
        const input = {
          id: decodeId(this.logId),
          coachComment: this.chatText,
        };

        if (this.logType == "NUTRITION") {
          const result = await this.$apollo.mutate({
            mutation: STAFF_UPDATE_NUTRITION_LOG_MUTATION,
            variables: {
              data: input,
            },
            refetchQueries: ["activitiesQuery", "nutritionQuery"],
          });
          this.messageSent = result.data?.staffUpdateUserActivity?.success;
        } else if (this.logType == "ACTIVITY" && this.log?.startDate) {
          const result = await this.$apollo.mutate({
            mutation: STAFF_UPDATE_USER_ACTIVITY_MUTATION,
            variables: {
              data: input,
            },
            refetchQueries: ["activitiesQuery", "nutritionQuery"],
          });
          this.messageSent = result.data?.staffUpdateUserActivity?.success;
        } else if (this.logType == "ACTIVITY" && this.log?.restDayDate) {
          const result = await this.$apollo.mutate({
            mutation: STAFF_UPDATE_USER_REST_DAY_MUTATION,
            variables: {
              data: input,
            },
            refetchQueries: ["activitiesQuery"],
          });
          this.messageSent = result.data?.staffUpdateUserRestDay?.success;
        }
        if (!this.sendChatMessage) {
          return;
        }
      }

      this.messageSent = false;
      this.loading = true;

      let attachmentIds = [];

      if (this.image && !this.removeImage) {
        const $vm = this;
        const ready = new Promise((resolve) => {
          urlToDataURL(this.image, async function(dataUrl) {
            $vm.remoteImageDataUrl = dataUrl;
            resolve();
          });
          return;
        });
        await ready;
        const blob = dataURLtoBlob(this.remoteImageDataUrl);
        const file = blobToFile(blob);

        const uploadResult = await this.$apollo.mutate({
          mutation: CREATE_MESSAGE_ATTACHMENTS_MUTATION,
          variables: {
            data: {
              files: [file],
              type: "IMAGE",
            },
          },
        });
        if (uploadResult.data.createMessageAttachmentsMutation.success) {
          attachmentIds = uploadResult.data.createMessageAttachmentsMutation.attachmentIds;
        }
      }

      let chatText = this.chatText;

      if (this.loggedType && this.loggedDate) {
        chatText = `<p><b>${this.loggedType} on ${this.loggedDate}</b><br>${this.chatText}</p>`;
      }

      const data = {
        chatId: decodeId(this.selectedCoachingChatId),
        text: chatText,
        attachmentIds: attachmentIds,
        sendEmailNotification: this.sendEmailNotification,
      };

      const result = await this.$apollo.mutate({
        mutation: UPSERT_MESSAGE_MUTATION,
        variables: {
          data: data,
        },
      });
      this.messageSent = result.data?.upsertMessageMutation?.success;
      this.loading = false;
    },
  },
  apollo: {
    chatsForUser: {
      query: CHATS_FOR_USER,
      fetchPolicy: "no-cache",
      variables() {
        return {
          type_In: ["COACH"],
          userId: this.userId,
        };
      },
      update(data) {
        const chats = extractNodes(data.chatsForUser.edges);
        return chats.map((item) => {
          return {
            id: decodeId(item.id),
            title: item.title,
          };
        });
      },
    },
  },
};
</script>

<style scoped>
.relative {
  position: relative;
}
.img-close-icon {
  position: absolute;
  top: -7px;
  right: -20px;
}
</style>
